import { useEffect, useRef } from 'react';
import { useWishlistStore } from '@/stores/wishlistStore';
import { useAuthStore } from '@/stores/authStore';
import { listWishlistItems, addWishlistItem, removeWishlistItemByProduct } from '@/api/wishlist';

/**
 * Syncs the local wishlist store with DynamoDB for signed-in users.
 * On login: fetches server state and merges with any local items (union).
 * On toggle: mirrors the change to DynamoDB immediately.
 */
export function useWishlistSync() {
  const user = useAuthStore((s) => s.user);
  const ids = useWishlistStore((s) => s.ids);
  const { toggle: localToggle } = useWishlistStore.getState();
  const prevIdsRef = useRef<string[]>([]);
  const syncedRef = useRef(false);
  const prevUserIdRef = useRef<string | null>(null);

  // On login (or userId change): load server wishlist, merge with local
  useEffect(() => {
    if (!user) {
      syncedRef.current = false;
      prevUserIdRef.current = null;
      return;
    }
    if (user.id === prevUserIdRef.current && syncedRef.current) return;

    prevUserIdRef.current = user.id;
    syncedRef.current = false;

    void (async () => {
      const serverItems = await listWishlistItems(user.id).catch(() => []);
      const serverIds = serverItems.map((i) => i.productId);
      const localIds = useWishlistStore.getState().ids;

      // Add local items not yet on server
      const toAdd = localIds.filter((id) => !serverIds.includes(id));
      await Promise.all(toAdd.map((id) => addWishlistItem(user.id, id).catch(() => null)));

      // Merge: union of server + local
      const mergedIds = Array.from(new Set([...serverIds, ...localIds]));
      useWishlistStore.setState({ ids: mergedIds });
      prevIdsRef.current = mergedIds;
      syncedRef.current = true;
    })();
  }, [user?.id]);

  // Mirror toggle changes to DynamoDB once initial sync is done
  useEffect(() => {
    if (!user || !syncedRef.current) {
      prevIdsRef.current = ids;
      return;
    }

    const prev = prevIdsRef.current;
    const added = ids.filter((id) => !prev.includes(id));
    const removed = prev.filter((id) => !ids.includes(id));

    added.forEach((id) => void addWishlistItem(user.id, id).catch(() => null));
    removed.forEach((id) => void removeWishlistItemByProduct(user.id, id).catch(() => null));

    prevIdsRef.current = ids;
  }, [ids, user]);
}
